import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { Component, Prop } from 'vue-property-decorator';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ValidationObserver } from 'vee-validate';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faIdBadge,
  faIdCard,
  faSlidersH,
  faSync,
  faPause,
} from '@fortawesome/free-solid-svg-icons';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import { Subscription } from '@subscriptions/interfaces/subscription';
import SubscriptionForm from '@subscriptions/components/forms/subscription-form/subscription-form.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import AuthorizationHelper from '@auth/services/authorization.helper';
import SendMessageUsingTemplateMixin from '@shared/components/tables/abstract-table/send-message-using-template-mixing';
import ModulesHelper from '@core/services/modules.helper';
import BulkMoveToTrashModal from '@shared/components/modals/bulk-move-to-trash-modal/bulk-move-to-trash-modal.vue';
import BulkRecoverModal from '@shared/components/modals/bulk-recover-modal/bulk-recover-modal.vue';
import ExtendedFilter from '@shared/components/filters/extended-filter/extended-filter.vue';
import CustomFilterSelector from '@shared/components/filters/custom-filter-selector/custom-filter-selector.vue';
import { AccessControlBadge } from '@access-control/interfaces/access-control-badge';
import BpcAccessControlBadgeForm from '@access-control/components/access-control-badge-form.vue';
import AdjustCreditsModal from '@subscriptions/components/modals/adjust-credits-modal/adjust-credits-modal.vue';
import moment from 'moment';
import BpcRecreditSubscriptionModal from '@subscriptions/components/modals/recredit-subscription-modal.vue';
import DownloadPdfUsingTemplateMixin from '@shared/components/tables/abstract-table/download-pdf-using-template-mixing';
import { FilterObject } from '@shared/interfaces/config';
import ShoppingCartProductConfirmationModal from '@accounting/components/shopping-cart/shopping-cart-product-confirmation-modal.vue';
import { ShoppingCartProduct } from '@accounting/interfaces/shopping-cart';
import AddPaymentModal from '@accounting/components/modals/add-payment-modal/add-payment-modal.vue';
import ReactivateSubscriptionModal from '@subscriptions/components/modals/reactivate-subscription-modal/reactivate-subscription-modal.vue';
import SuspendSubscriptionModal from '@subscriptions/components/modals/suspend-subscription-modal/suspend-subscription-modal.vue';
import { AccessControlConfigInterface } from '@core/interfaces/access-control-config-interface';
import ResyncFinddocProductModal from '@access-control/components/modals/resync-finddoc-product-modal/resync-finddoc-product-modal.vue';
import BulkSendPaymentRequestModal from '@shared/components/modals/send-bulk-product-payment-request-message-drawer/send-bulk-product-payment-request-message-drawer.vue';
import BulkSendPaymentReminderModal from '@shared/components/modals/send-bulk-product-payment-reminder-message-drawer/send-bulk-product-payment-reminder-message-drawer.vue';
import RenewSubscriptionsModal from '@subscriptions/components/modals/renew-subscriptions-modal/renew-subscriptions-modal.vue';

library.add(faSync, faSlidersH, faIdBadge, faIdCard, faPause);

@Component({
  name: 'bpc-subscription-table',
  components: {
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-std-form': StdForm,
    'bpc-std-filter': StdFilter,
    ValidationObserver,
    'bpc-drawer-modal': DrawerModal,
    'bpc-subscription-form': SubscriptionForm,
    'bpc-bulk-move-to-trash-modal': BulkMoveToTrashModal,
    'bpc-bulk-recover-modal': BulkRecoverModal,
    'bpc-extended-filter': ExtendedFilter,
    'bpc-custom-filter-selector': CustomFilterSelector,
    BpcAccessControlBadgeForm,
    'bpc-adjust-credits-modal': AdjustCreditsModal,
    BpcRecreditSubscriptionModal,
    'bpc-shopping-cart-product-confirmation-modal': ShoppingCartProductConfirmationModal,
    'bc-add-payment-modal': AddPaymentModal,
    'bc-reactivate-subscription-modal': ReactivateSubscriptionModal,
    'bc-suspend-subscription-modal': SuspendSubscriptionModal,
    'bpc-resync-subscriptions-modal': ResyncFinddocProductModal,
    'bpc-bulk-send-payment-request-modal': BulkSendPaymentRequestModal,
    'bpc-bulk-send-payment-reminder-modal': BulkSendPaymentReminderModal,
    'bpc-renew-subscriptions-modal': RenewSubscriptionsModal,
  },
})
export default class SubscriptionTable extends mixins(
  AbstractTable,
  SendMessageUsingTemplateMixin,
  DownloadPdfUsingTemplateMixin,
) {
  declare $refs: AbstractTable['$refs'] & {
    stdSubscriptionForm: InstanceType<typeof SubscriptionForm> & {
      saveEntity(): void;
      setEntityAttributesAfterSave(): void;
      formModel: any;
    };
    stdCancelForm: InstanceType<typeof StdForm> & {
      formModel: any;
      formProvider: EntityFormProvider;
    };
  };
  @Prop(String) declare readonly displayConfigName: string;
  @Prop(String) readonly title: string;
  @Prop(String) readonly subtitle: string;
  @Prop({ type: String, default: null }) readonly createFormConfigName: string;
  @Prop({ type: String, default: 'edit-subscription' }) readonly editFormConfigName: string;
  @Prop({ type: String, default: 'cancel-subscriptions' }) readonly cancelFormConfigName: string;
  @Prop({ type: Array, default: () => ['part1', 'part2'] })
  declare readonly selectConfigName: string[];
  @Prop({ type: Boolean, default: false }) readonly showActionButtonText: boolean;
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly filtersExcluded: string[];
  @Prop({ type: Boolean, default: false }) readonly profileTable: boolean;
  @Prop(String) readonly fetchPath: string;

  public entity: Subscription = null;
  public entityName = 'subscription';
  public pluralEntityName = 'subscriptions';
  public showDrawerSettingSection: boolean = false;
  public showSettingForm: boolean = false;
  public permissionEntityNameToUse = 'subscriptions';
  public selectedRowEntity = null;
  public isCancelingEntity: boolean = false;
  // Add payment modal
  public showPaymentModal: boolean = false;
  public showSuspendModal: boolean = false;
  public showReactivateModal: boolean = false;
  // Resync modal data
  showResyncModal: boolean = false;
  showRenewModal: boolean = false;
  resyncAlertKey: number = 0;

  //Bulk messages data
  bulkSelectedContactIds: number[] = [];
  bulkSelectedSubscriptionIds: number[] = [];
  public showBulkSendPaymentRequestModal = false;
  public showBulkSendPaymentReminderModal = false;

  onBulkSendPaymentRequest(selectedIds: number[]) {
    this.bulkSelectedSubscriptionIds = selectedIds;
    this.bulkSelectedContactIds = selectedIds
      .map(itemId => {
        const item = (this.$refs.provider.items as any).find(i => i.id === itemId);
        return item?.contact_id;
      })
      .filter(Boolean);
    this.showBulkSendPaymentRequestModal = true;
  }

  onCloseBulkSendPaymentRequestModal() {
    this.showBulkSendPaymentRequestModal = false;
  }

  onBulkSendPaymentReminder(selectedIds: number[]) {
    this.bulkSelectedSubscriptionIds = selectedIds;
    this.bulkSelectedContactIds = selectedIds
      .map(itemId => {
        const item = (this.$refs.provider.items as any).find(i => i.id === itemId);
        return item?.contact_id;
      })
      .filter(Boolean);

    this.showBulkSendPaymentReminderModal = true;
  }

  onCloseBulkSendPaymentReminderModal() {
    this.showBulkSendPaymentReminderModal = false;
  }

  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'filtersLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  public customPageActions = {
    resync: {
      activated: false,
      type: 'action',
      icon: 'sync',
      variant: 'primary',
      text: 'resync',
      show: false,
      event: 'resync',
      selectElements: true,
      tag: 'left',
    },
    renew: {
      activated: false,
      type: 'action',
      icon: 'sync',
      variant: 'primary',
      text: 'renew',
      show: false,
      event: 'renew',
      selectElements: true,
      tag: 'left',
    },
    bulkActions: {
      activated: false,
      type: 'dropdown',
      icon: 'cubes',
      variant: 'info',
      text: 'bulk_actions',
      show: false,
      event: '',
      selectElements: true,
      tag: 'bulk',
      permissions: [],
      children: {
        bulkEdit: {
          activated: false,
          type: 'dropdown-item',
          icon: 'edit',
          variant: 'info',
          text: 'bulk-edit',
          show: true,
          event: 'bulk-edit',
          selectElements: true,
          tag: 'left',
          permissions: ['bulk_edit'],
          withoutDivider: false,
        },
        divider1: {
          activated: false,
          type: 'head',
          text: null,
          show: true,
          withoutDivider: false,
          permissions: [],
        },
        bulkPaymentRequest: {
          activated: false,
          type: 'dropdown-item',
          icon: 'envelope', //'times',
          variant: 'info',
          text: 'send_payment_request',
          show: false,
          event: 'bulk-send-payment-request',
          selectElements: true,
          tag: 'right',
          permissions: ['bulk_message'],
        },
        bulkPaymentReminder: {
          activated: false,
          type: 'dropdown-item',
          icon: 'envelope', //'times',
          variant: 'info',
          text: 'send_payment_reminder',
          show: false,
          event: 'bulk-send-payment-reminder',
          selectElements: true,
          tag: 'right',
          permissions: ['bulk_message'],
        },
        bulkSendMessageUsingTemplate: {
          activated: false,
          type: 'dropdown-item',
          icon: 'envelope', //'times',
          variant: 'info',
          text: 'send_message_using_template',
          show: false,
          event: 'bulk-send-message-using-template',
          selectElements: true,
          tag: 'right',
          permissions: ['bulk_message'],
          withoutDivider: false,
        },
        divider2: {
          activated: false,
          type: 'head',
          text: null,
          show: true,
          withoutDivider: false,
          permissions: [],
        },
        bulkSoftDelete: {
          activated: false,
          type: 'dropdown-item',
          icon: 'trash',
          variant: 'danger',
          text: 'soft-delete',
          show: false,
          event: 'bulk-soft-delete',
          selectElements: true,
          tag: 'left',
          permissions: ['bulk_delete'],
        },
      },
    },
    bulkCancel: {
      activated: false,
      type: 'action',
      icon: 'ban', //'times',
      variant: 'danger',
      text: 'cancel',
      show: false,
      event: 'bulk-cancel',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_cancel'],
    },
  };

  onResync() {
    this.showResyncModal = true;
    this.$bvModal.show('resync-modal');
  }

  onCloseResyncModal() {
    this.showResyncModal = false;
    this.$refs.provider.getEntities();
  }

  onRenew() {
    this.showRenewModal = true;
    this.$bvModal.show('renew-modal');
  }

  onCloseRenewModal() {
    this.showRenewModal = false;
  }

  onProcessingRenewEnd() {
    this.showRenewModal = false;
    this.$refs.provider.getEntities();
  }

  closeResyncProgressBar() {
    if (this.isProcessingResyncEnded) {
      this.$store.dispatch('subscriptionResyncStore/closeResyncProccess');
    }
  }

  onStartProcessingResync() {
    this.resyncAlertKey = this.resyncAlertKey + 1;
    this.$store.dispatch('subscriptionResyncStore/startResyncProcess');
    this.onCloseResyncModal();
  }

  listenToWebsocketResyncChannel(): void {
    this.$bpcEcho
      .listenResyncSubscriptionChannel()
      .listen('.BPC\\Subscriptions\\Events\\SubscriptionResyncStarted', data => {
        this.$store.dispatch('subscriptionResyncStore/updateResyncStatus', {
          running: true,
          ended: false,
          failed: false,
          totalCount: data.totalCount || 0,
          processedCount: 0,
        });
      })
      .listen('.BPC\\Subscriptions\\Events\\SubscriptionResyncProcessed', data => {
        this.$store.dispatch('subscriptionResyncStore/updateResyncStatus', {
          running: true,
          ended: false,
          failed: false,
          totalCount: data.totalCount || 0,
          processedCount: data.processedCount || 0,
          isResyncClosed: false,
        });
      })
      .listen('.BPC\\Subscriptions\\Events\\SubscriptionResyncEnded', data => {
        this.$store.dispatch('subscriptionResyncStore/updateResyncStatus', {
          running: false,
          ended: true,
          failed: false,
          totalCount: data.totalCount || 0,
          processedCount: data.processedCount || 0,
          isResyncClosed: false,
        });
        this.$refs.provider.getEntities();
      });
  }

  leaveWebsocketResyncChannel(): void {
    this.$bpcEcho.leaveResyncSubscriptionChannel();
  }

  customOnPageAction($event: string) {
    switch ($event) {
      case 'bulk-cancel':
        this.onOpenCancelModal(null);
        break;
      case 'bulk-send-message-using-template':
        this.onBulkSendMessageUsingTemplate(this.selection);
        break;
      case 'resync':
        this.onResync();
        break;
      case 'bulk-send-payment-request':
        this.onBulkSendPaymentRequest(this.selection);
        break;
      case 'bulk-send-payment-reminder':
        this.onBulkSendPaymentReminder(this.selection);
        break;
      case 'renew':
        this.onRenew();
        break;
    }
  }

  extendUpdatePageActions() {
    const activatedPageActions = this.config?.data?.activatedPageActions || [];
    if (this.config) {
      for (const activatedPageAction of activatedPageActions) {
        if (this.customPageActions[activatedPageAction]) {
          this.customPageActions[activatedPageAction].activated = true;
        }
      }
    }

    this.customPageActions.bulkCancel.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    // Bulk actions
    this.customPageActions.bulkActions.activated =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkActions') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    this.customPageActions.bulkActions.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkActions') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    this.customPageActions.bulkActions.children.bulkEdit.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkEdit') &&
      this.$authorizationService.hasPermissionForAction(
        this.permissionEntityNameToUse,
        'bulk_edit',
      );

    this.customPageActions.bulkActions.children.bulkSoftDelete.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkSoftDelete') &&
      this.$authorizationService.hasPermissionForAction(
        this.permissionEntityNameToUse,
        'bulk_delete',
      );

    // TODO: Add more checks when implementing this function
    this.customPageActions.bulkActions.children.bulkPaymentRequest.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkPaymentRequest') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    // TODO: Add more checks when implementing this function
    this.customPageActions.bulkActions.children.bulkPaymentReminder.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkPaymentReminder') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    this.customPageActions.bulkActions.children.bulkSendMessageUsingTemplate.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkSendMessageUsingTemplate') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);

    this.customPageActions.resync.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.isFinddocAccessControlService;

    this.customPageActions.renew.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    // Check Permissions
    for (const activatedPageAction of activatedPageActions) {
      if (
        this.customPageActions[activatedPageAction] &&
        this.customPageActions[activatedPageAction]['permissions'] &&
        this.customPageActions[activatedPageAction]['permissions'].length
      ) {
        const permissions = [];
        this.customPageActions[activatedPageAction]['permissions'].forEach(permissionName => {
          permissions.push(
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
              ),
            },
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
                true,
              ),
            },
          );
        });
        this.customPageActions[activatedPageAction][
          'activated'
        ] = this.$authorizationService.hasPermissions(permissions);
      }
    }
  }

  getEntities() {
    this.$refs.provider.getEntities();
  }

  get isProcessingResyncFailed(): boolean {
    return this.$store.getters['subscriptionResyncStore/isProcessingResyncFailed'];
  }
  get isProcessingResyncEnded(): boolean {
    return this.$store.getters['subscriptionResyncStore/isProcessingResyncEnded'];
  }
  get totalCount(): number {
    return this.$store.getters['subscriptionResyncStore/totalCount'];
  }
  get processedCount(): number {
    return this.$store.getters['subscriptionResyncStore/processedCount'];
  }
  get isProcessingResyncRunning(): boolean {
    return this.$store.getters['subscriptionResyncStore/isProcessingResyncRunning'];
  }
  get isResyncClosed(): boolean {
    return this.$store.getters['subscriptionResyncStore/isResyncClosed'];
  }

  get pageActions() {
    return {
      ...this.customPageActions,
      ...this.defaultPageActions,
    };
  }

  get cancelModalTitle() {
    return this.$t('global.modals.bulk-cancel.title', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  get cancelModalBody() {
    return this.$t('global.modals.bulk-cancel.body', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  get resyncAlertStatus(): string {
    if (this.isProcessingResyncRunning) {
      return 'warning';
    } else if (this.isProcessingResyncEnded) {
      return 'success';
    } else if (this.isProcessingResyncFailed) {
      return 'danger';
    } else {
      return 'info';
    }
  }

  onOpenDeleteModal(entity) {
    this.$refs.softDeleteConfirmationModal.onOpenModal(entity.id);
  }

  onOpenCancelModal(entity) {
    this.selectedRowEntity = entity;
    this.$bvModal.show('modal-cancel-subscriptions');
    this.onFormClose();
  }

  onCloseCancelModal() {
    this.selectedRowEntity = null;
    this.isCancelingEntity = false;
    this.$bvModal.hide('modal-cancel-subscriptions');
  }

  onStatusChanged() {
    this.showReactivateModal = false;
    this.showSuspendModal = false;
    this.$refs.provider.getEntities();
    this.onFormClose();
  }

  onConfirmCancelModal() {
    this.isCancelingEntity = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('subscription');
    this.$bpcApi
      .post(basePath + '/bulk-cancel', {
        subscriptionIds: this.selection,
        cancel_reason: this.$authorizationService.hasPermissionForAction(
          this.permissionEntityNameToUse,
          'cancel',
        )
          ? this.$refs.stdCancelForm.formModel['cancel_reason']
          : 'customer',
        cancel_reason_description: this.$refs.stdCancelForm.formModel['cancel_reason_description'],
        notify_by_email:
          'notify_by_email' in this.$refs.stdCancelForm.formModel
            ? this.$refs.stdCancelForm.formModel['notify_by_email']
            : true,
      })
      .subscribe(
        res => {
          this.$refs.provider.getEntities();
          this.$bvModal.hide('modal-cancel-subscriptions');
          this.isCancelingEntity = false;
        },
        error => {
          this.$bvModal.hide('modal-cancel-subscriptions');
          this.isCancelingEntity = false;
        },
      );
  }

  onOpenPaymentModal(entity) {
    this.showPaymentModal = true;
  }

  onCloseAddpaymentModal() {
    this.showPaymentModal = false;
  }

  onPaymentCreated() {
    this.showPaymentModal = false;
    this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
    this.i++; // refresh stdForm
    this.$refs.provider.getEntities(); // refresh table
  }

  public beforeDefaultRowActions = {
    recredit: {
      activated: true,
      type: 'action',
      icon: 'money-bill-wave',
      show: true,
      event: 'recredit',
      text: 'recredit',
      permissions: ['update'],
    },
    payProduct: {
      activated: false,
      type: 'action',
      icon: 'credit-card',
      show: false,
      event: 'pay-product',
      text: 'pay',
      permissions: ['pay_product'],
    },
  };
  public customRowActions = {
    sendMessageUsingTemplate: {
      activated: true,
      type: 'action',
      icon: 'envelope',
      show: true,
      event: 'send-message-using-template',
      text: 'send_message_using_template',
      permissions: ['send'],
    },
    downloadPdfUsingTemplate: {
      activated: true,
      type: 'action',
      icon: 'download',
      show: true,
      event: 'download-pdf-using-template',
      text: 'download_attestation_button_label',
      permissions: ['show'],
    },
  };

  get rowActions() {
    return {
      ...this.beforeDefaultRowActions,
      ...this.defaultRowActions,
      ...this.customRowActions,
    };
  }

  get isOnline(): boolean {
    return this.$route.name?.includes('profile-');
  }

  customOnRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'cancel':
        this.onOpenCancelRowModal($event.item);
        break;
      case 'send-message-using-template':
        this.onSendMessageUsingTemplate($event.item.item);
        break;
      case 'recredit':
        this.onRecredit($event.item.item);
        break;
      case 'download-pdf-using-template':
        this.onDownloadPdfUsingTemplate($event.item.item);
        break;
      case 'pay-product':
        this.onPayProduct($event.item.item);
        break;
    }
  }

  // Adapted specifically for attestations
  templateSelectorCustomFilters: FilterObject[] = [];
  async onDownloadPdfUsingTemplate(item) {
    this.downloadPdfUsingTemplateSelectedItemIds = item?.id ? [item?.id] : [];
    this.templateSelectorCustomFilters = [
      {
        filterField: 'subscription_attestations',
        value: item.id,
      },
    ];
    this.downloadPdfUsingTemplateCleanErrors();
    this.openDownloadPdfUsingTemplateModal();
  }
  async closeDownloadPdfUsingTemplateModal() {
    this.isDownloadingPdfUsingTemplate = false;
    this.showDownloadPdfUsingTemplateModal = false;
    this.templateSelectorCustomFilters = [];
    this.downloadPdfUsingTemplateCleanErrors();
  }

  onRecredit(item) {
    this.onShowRecreditModal(item.id);
  }

  onPayProduct(item) {
    this.entity = item;
    this.onOpenPaymentModal(item);
  }

  extendUpdateRowActions(entity: any): void {
    entity.actions.cancel.show =
      entity.status !== 'cancelled' &&
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      (this.$authorizationService.hasPermission('manage_subscriptions') ||
        entity.subscription_with_payment === false ||
        entity.options?.fixed_options.allow_cancel_subscription);

    entity.actions.recredit.show =
      entity.can_recredit &&
      this.$authorizationService.hasPermissions([
        'manage_subscriptions',
        'manage_own_subscriptions',
      ]) &&
      this.shoppingCartActivated;

    entity.actions.sendMessageUsingTemplate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);

    entity.actions.downloadPdfUsingTemplate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      entity.status === 'ongoing' &&
      !!entity.has_attestations &&
      (!entity.payment_status || entity.payment_status === 'paid');

    // Add Payment to product
    const canAddPayments = this.$authorizationService.hasPermissionForAction(
      'payments',
      'pay_product',
    );

    entity.actions.payProduct.show =
      this.tableIsNotFilteredByTrashedElements() &&
      canAddPayments &&
      entity.payment_status === 'unpaid' &&
      entity.status !== 'cancelled';

    entity.actions.payProduct.activated = !!entity.actions.payProduct.activated && canAddPayments;
  }

  hasPermissionToCancel(): boolean {
    return this.$authorizationService.hasPermissionForAction(
      this.permissionEntityNameToUse,
      'cancel',
    );
  }

  onOpenCancelRowModal(item) {
    this.selectedRowEntity = item.item;
    this.$bvModal.show('modal-cancel-subscriptions');
  }

  showRecreditForm = false;
  showShoppingCartConfirmationModal = false;
  recreditSubscriptionId = null;
  createdShoppingCartProduct: ShoppingCartProduct = null;

  onShowRecreditModal(subscriptionId) {
    this.recreditSubscriptionId = subscriptionId;
    this.showRecreditForm = true;
  }

  onSubscriptionCreatedInFrontOffice(product: ShoppingCartProduct) {
    this.createdShoppingCartProduct = product;
    this.showShoppingCartConfirmationModal = true;
    this.$root.$emit('refresh-orders');
  }

  onConfirmCancelRowModal() {
    this.isCancelingEntity = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('subscription');
    const relativePath = '/cancel/' + this.selectedRowEntity.id;

    this.$bpcApi
      .put(basePath + relativePath, {
        id: this.selectedRowEntity.id,
        cancel_reason: this.$authorizationService.hasPermissionForAction(
          this.permissionEntityNameToUse,
          'cancel',
        )
          ? this.$refs.stdCancelForm.formModel['cancel_reason']
          : 'customer',
        cancel_reason_description: this.$refs.stdCancelForm.formModel['cancel_reason_description'],
        cancel_orders: this.$refs.stdCancelForm.formModel['cancel_orders'],
        notify_by_email:
          'notify_by_email' in this.$refs.stdCancelForm.formModel
            ? this.$refs.stdCancelForm.formModel['notify_by_email']
            : true,
      })
      .subscribe(
        res => {
          this.$refs.provider.getEntities();
          this.$bvModal.hide('modal-cancel-subscriptions');
          this.selectedRowEntity = null;
          this.isCancelingEntity = false;
        },
        error => {
          this.$bvModal.hide('modal-cancel-subscriptions');
          this.selectedRowEntity = null;
          this.isCancelingEntity = false;
        },
      );
  }

  setEntityAttributesAfterSave() {
    this.$refs.stdSubscriptionForm.setEntityAttributesAfterSave();
    this.showForm = true;
    this.showDrawerSection = true;
  }

  onFormSave(next: string = '') {
    if (
      this.$refs.stdSubscriptionForm?.formModel?.status === 'cancelled' &&
      this.entity.status !== 'cancelled'
    ) {
      this.$bvModal.msgBoxOk(this.$t('global.use_corresponding_cancel_action') as string, {
        okTitle: this.$t('global.buttons.accept') as string,
        title: ((this.$t('global.modals.edit') as string) +
          ' ' +
          this.$t('global.models.booking.name')) as string,
      });
    } else {
      this.nextAction = next;
      this.$refs.stdSubscriptionForm.saveEntity();
    }
  }

  onFormSaveAndPay() {
    this.onFormSave('pay');
  }

  onFormSaveAndNew() {
    this.nextAction = 'new';
    this.$refs.stdSubscriptionForm.saveEntity();
  }

  onFormSaved($event: any = null) {
    if (this.nextAction === 'new') {
      this.setEntityAttributesAfterSave();
      this.$refs.provider.getEntities();
    } else if (this.nextAction === 'pay') {
      const orders = [];
      const contacts = [];
      const priceCategories = [];
      $event.data?.forEach(subscription => {
        subscription.orders.forEach(order => orders.push(order));
        if (!contacts.includes(subscription.contact_id)) {
          contacts.push(subscription.contact_id);
        }
        if (!priceCategories.includes(subscription.price_category_id)) {
          priceCategories.push(subscription.price_category_id);
        }
      });
      this.ordersAvailableToPosCheckout = orders;
      this.selectedOrdersToPosCheckout = orders;
      this.selectedContactIdToPosCheckout = contacts.length > 1 ? null : contacts[0];
      this.selectedPriceCategoryIdToPosCheckout =
        priceCategories.length > 1 ? null : priceCategories[0];
      this.selectedInvoiceIdToPosCheckout =
        this.$store.getters['pos/currentTicket']?.status === 'to_pay'
          ? this.$store.getters['pos/currentTicket']
          : null;
      this.onFormClose();
      this.$refs.provider.getEntities();
      this.openExportPosCheckoutModal();
    } else {
      this.onFormClose();
      this.$refs.provider.getEntities();
    }
  }

  get subscriptionFormConfigName() {
    if (this.entity) {
      return this.editFormConfigName ? this.editFormConfigName : this.formConfigName;
    } else {
      return this.createFormConfigName ? this.createFormConfigName : this.formConfigName;
    }
  }

  get shoppingCartActivated(): boolean {
    // True for admin table, check boolean for profile table.
    return this.profileTable ? this.$store.getters['centerSettings/shoppingCartActivated'] : true;
  }

  /* Lifecycle hooks */

  // created() {}
  mounted() {
    this.exportCheckoutModalId = this.generateUUIDv4();
    this.$root.$on('refresh-table-after-add-orders-to-pos', () => {
      this.refreshEntitiesAfterOrdersAddedToPosCheckout();
    });

    window.addEventListener('refresh-subscription-tables', this.handleRefreshEvent);

    // For messages with templates
    const templatesRequest = [this.getAllowedTemplatesForSendMessage()];

    Promise.all(templatesRequest).then(res => {
      this.updateRowActions();
      this.updatePageActions();
    });

    if (this.isFinddocAccessControlService) {
      this.listenToWebsocketResyncChannel();
    }
  }
  // updated() {}
  destroyed() {
    if (this.$refs.entityTable) {
      this.$refs.entityTable.$off('on-click-display');
    }
    this.$root.$off('refresh-table-after-add-orders-to-pos');

    window.removeEventListener('refresh-subscription-tables', this.handleRefreshEvent);

    if (this.isFinddocAccessControlService) {
      if (!this.isProcessingResyncRunning) {
        this.leaveWebsocketResyncChannel();
      }
    }
  }

  // Bulk soft delete modal (move to trash)
  showBulkMoveToTrashModal = false;
  onBulkSoftDelete() {
    this.showBulkMoveToTrashModal = true;
  }
  onCLoseBulkSoftDelete() {
    this.showBulkMoveToTrashModal = false;
  }
  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    this.showDrawerSection = false;
    this.$refs.provider.getEntities();
  }
  // Here, put the same fields as you put in dataToShowForBulkTrashedEntity in your entity Service
  bulkMoveToTrashDataToShow = [['code', 'comment'], ['contact'], ['article'], ['priceCategory']];

  // Bulk restore modal (recover)

  showBulkRecoverModal = false;

  onBulkRecover() {
    this.showBulkRecoverModal = true;
  }

  onCLoseBulkRecover() {
    this.showBulkRecoverModal = false;
  }

  onSuccessBulkRecover() {
    this.$refs.provider.getEntities();
  }

  bulkRecoverDataToShow = [['code', 'comment'], ['contact'], ['article'], ['priceCategory']];

  get selectedRowEntityId() {
    return this.entity?.id;
  }

  get hasAccessControlModuleActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ACCESS_CONTROL_MODULE_NAME);
  }

  get accessControlConfig(): AccessControlConfigInterface {
    return this.$store.getters['centerSettings/accessControlConfig'];
  }

  get isInternalAccessControlService(): boolean {
    return this.accessControlConfig?.access_control_service === 'internal';
  }

  get isFinddocAccessControlService(): boolean {
    return this.accessControlConfig?.access_control_service === 'finddoc';
  }

  get showSuspendAction() {
    return this.entity?.status === 'ongoing';
  }

  get showReactivateAction() {
    return this.entity?.status === 'suspended';
  }

  get canCreateAccessControlBadges() {
    const permissionName = AuthorizationHelper.getPermissionName('store', 'access_control_badges');
    return this.$authorizationService.hasPermissions([permissionName]);
  }

  get showAddPaymentAction() {
    return (
      this.entity?.orders &&
      this.entity?.orders.some(
        order =>
          order.status === 'waiting' ||
          order.status === 'shopping_cart' ||
          order.status === 'invoiced' ||
          order.status === 'payment_process',
      )
    );
  }

  get initialAccessControlBadgeData(): Partial<AccessControlBadge> {
    return {
      status: 'active',
      contact_id: Number(this.entity?.contact_id),
      type: 'rfid',
    };
  }
  isSavingAccessControlBadge: boolean = false;
  labelSaveAccessControlBadgeButton: string =
    'global.access_control_badge.label_to_add_from_contact';

  timestamp = moment().unix();

  get accessControlBadgeFormCancelPortalTarget() {
    return `access-control-badge-form-cancel-action-${this.timestamp}`;
  }

  get accessControlBadgeFormSavePortalTarget() {
    return `access-control-badge-form-actions-${this.timestamp}`;
  }

  get titleAddAccessControlBadgeModal(): string {
    return (
      this.$t('global.modals.create') + ' ' + this.$t('global.models.access-control-badge.name')
    );
  }
  onClickCreateAccessControlBadge() {
    this.$bvModal.show('modal-add-access-control-badge');
  }

  onCloseAccessControlBadgeModal() {
    this.$bvModal.hide('modal-add-access-control-badge');
  }

  onOpenSuspendModal($event) {
    this.showSuspendModal = true;
  }

  onOpenReactivateModal($event) {
    this.showReactivateModal = true;
  }

  onSavedAccessControlBadge($event) {
    this.$root.$emit('new-badge-created-from-subscription', $event.data);
  }

  onAdjustedRemainingCredits($event) {
    this.i++; // Refresh stdForm
    this.$refs.provider.getEntities(); // refresh table
  }

  created() {
    // Remove default actions to be managed in other position (dropdown more actions)
    delete this.defaultPageActions.bulkSoftDelete;
    delete this.defaultPageActions.bulkEdit;
  }

  // Override to be able to manage the new position of some default actions
  updatePageActions() {
    if (this.config) {
      for (const activatedPageAction of this.config.data.activatedPageActions) {
        if (this.pageActions[activatedPageAction]) {
          this.pageActions[activatedPageAction].activated = true;
        }
      }
    }
    this.pageActions.bulkDelete.show =
      this.$refs.provider &&
      (this.$refs.provider.queryConfig.params.status === 'trash' ||
        this.$refs.provider.queryConfig.params.onlyTrashed)
        ? true
        : false;
    this.pageActions.bulkRecover.show =
      this.$refs.provider &&
      (this.$refs.provider.queryConfig.params.status === 'trash' ||
        this.$refs.provider.queryConfig.params.onlyTrashed)
        ? true
        : false;

    // POS Checkout
    const hasPosCheckoutPermission = this.$authorizationService.hasPermissionForAction(
      'pos_sessions',
      'bulk_pos_checkout',
      true,
    );
    this.pageActions.bulkPosCheckout.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$refs.provider.items?.find(item => item['orders']?.length > 0) &&
      this.$refs.provider.items?.find(item =>
        item['orders'].some(
          order =>
            order.status == 'waiting' ||
            (order.type === 'attendee' && order.status === 'shopping_cart'),
        ),
      ) &&
      hasPosCheckoutPermission &&
      !!this.openPosSession;

    // Check Permissions
    const activatedPageActions = this.config?.data?.activatedPageActions || [];
    for (const activatedPageAction of activatedPageActions) {
      if (
        this.pageActions[activatedPageAction] &&
        this.pageActions[activatedPageAction]['permissions']
      ) {
        this.checkPermission(activatedPageAction);
      }
    }
    this.extendUpdatePageActions();
  }

  handleRefreshEvent = () => {
    if (this.$refs.provider && typeof this.$refs.provider.getEntities === 'function') {
      this.$refs.provider.getEntities();
    }
  };
}
